import React, { useEffect, useRef, useState } from 'react';
import { Modal, Notice, Icon } from './components';

export const documentUrl = (studentId, name) => `/backend/api/exchanges/students/${encodeURIComponent(studentId)}/documents/${encodeURIComponent(name)}`;
export const documentsArchiveUrl = studentId => `/backend/api/exchanges/students/${encodeURIComponent(studentId)}/documents-archive`;

function PdfPreview({ data }) {
  const canvas = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [rendering, setRendering] = useState(true);
  useEffect(() => {
    let active = true;
    let task;
    import('pdfjs-dist/webpack.mjs').then(({ getDocument }) => {
      if (!active) return;
      task = getDocument({ data: data.slice(), isEvalSupported: false });
      return task.promise.then(document => { if (active) setPdf(document); });
    }).catch(() => { if (active) setError('Ce PDF ne peut pas être affiché. Vous pouvez le télécharger pour le consulter.'); });
    return () => { active = false; if (task) task.destroy().catch(() => {}); };
  }, [data]);
  useEffect(() => {
    if (!pdf) return undefined;
    let active = true;
    let renderTask;
    setRendering(true);
    setError('');
    pdf.getPage(page).then(pdfPage => {
      if (!active) return;
      const viewport = pdfPage.getViewport({ scale: 1 });
      const scaled = pdfPage.getViewport({ scale: Math.min(2, 1400 / viewport.width) });
      const element = canvas.current;
      element.width = scaled.width;
      element.height = scaled.height;
      renderTask = pdfPage.render({ canvasContext: element.getContext('2d'), viewport: scaled });
      return renderTask.promise.then(() => { if (active) setRendering(false); });
    }).catch(error => { if (active && error.name !== 'RenderingCancelledException') { setError('Impossible d’afficher cette page.'); setRendering(false); } });
    return () => { active = false; renderTask?.cancel(); };
  }, [pdf, page]);
  return <>
    <Notice>{error}</Notice>
    {pdf && <div className="ex-preview-pages">
      <button className="ex-button" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Page précédente</button>
      <span aria-live="polite">Page {page} / {pdf.numPages}</span>
      <button className="ex-button" disabled={page >= pdf.numPages} onClick={() => setPage(value => value + 1)}>Page suivante</button>
    </div>}
    {rendering && !error && <p role="status">Chargement du PDF…</p>}
    <canvas ref={canvas} className="ex-preview-canvas" hidden={rendering || Boolean(error)} aria-label={`Page ${page} du document PDF`} role="img" />
  </>;
}

function DocumentContent({ studentId, document }) {
  const [content, setContent] = useState(null);
  const [error, setError] = useState('');
  const extension = document.name.split('.').pop().toLowerCase();
  const supported = ['png', 'jpg', 'jpeg', 'pdf'].includes(extension);
  useEffect(() => {
    if (!supported) return undefined;
    const controller = new AbortController();
    let objectUrl;
    let active = true;
    async function load() {
      try {
        const response = await fetch(documentUrl(studentId, document.name), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error(response.status === 401 || response.status === 403 ? 'Votre session a expiré ou ne permet pas de consulter cette pièce. Reconnectez-vous.' : 'Impossible de charger cette pièce.');
        const blob = await response.blob();
        if (extension === 'pdf') {
          const data = new Uint8Array(await blob.arrayBuffer());
          if (active) setContent({ data });
        } else if (active) {
          objectUrl = URL.createObjectURL(new Blob([blob], { type: extension === 'png' ? 'image/png' : 'image/jpeg' }));
          setContent({ url: objectUrl });
        }
      } catch (error) { if (active && error.name !== 'AbortError') setError(error.message); }
    }
    load();
    return () => { active = false; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [studentId, document.name, extension, supported]);
  if (!supported) return <p>Aperçu indisponible pour ce format. Vous pouvez télécharger la pièce.</p>;
  if (error) return <Notice>{error}</Notice>;
  if (!content) return <p role="status">Chargement de la pièce…</p>;
  return extension === 'pdf' ? <PdfPreview data={content.data} /> : <img className="ex-preview-image" src={content.url} alt={document.name} onError={() => setError('Cette image ne peut pas être affichée. Vous pouvez la télécharger.')} />;
}

export function previewItems(student) {
  const questions = (student.dossier?.data || []).flatMap(entry => (entry.criteres || []).filter(critere => Number(critere.id) !== 50)
    .flatMap(critere => (critere.sousCriteres || []).flatMap(section => (section.questions || []).filter(question => question.type === 'file').map(question => ({
      question, target: { entryId: entry.id, critereId: critere.id, sectionId: section.id, questionId: question.id }
    })))));
  const documents = student.documents || [];
  const items = documents.map(document => {
    const field = questions.find(({ question }) => question.nom === document.name.replace(/\.[^.]+$/, ''));
    return { key: document.name, document, ...field, label: field?.question.etiquette || document.name };
  });
  for (const field of questions) {
    if (!documents.some(document => document.name.replace(/\.[^.]+$/, '') === field.question.nom)) {
      items.push({ key: `missing:${Object.values(field.target).join('/')}`, ...field, label: field.question.etiquette || field.question.nom });
    }
  }
  return items;
}

function readFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('Impossible de lire ce fichier.'));
    reader.readAsDataURL(file);
  });
}

export default function DocumentViewer({ student, api, initialIndex, onUpdated, onClose, embedded = false, active = true, closeGuard }) {
  const items = previewItems(student);
  const [selectedKey, setSelectedKey] = useState(() => items[initialIndex]?.key);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const fileInput = useRef(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const index = Math.max(0, items.findIndex(item => item.key === selectedKey));
  const item = items[index];
  useEffect(() => {
    if (!closeGuard) return undefined;
    closeGuard.current = () => !busyRef.current;
    return () => { closeGuard.current = null; };
  }, [closeGuard]);
  function move(nextIndex) {
    if (busyRef.current || !items[nextIndex]) return;
    setSelectedKey(items[nextIndex].key); setError(''); setMessage('');
  }
  useEffect(() => {
    const keydown = event => {
      if (busyRef.current || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const nextIndex = Math.max(0, Math.min(items.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)));
        if (items[nextIndex]) { setSelectedKey(items[nextIndex].key); setError(''); setMessage(''); }
      }
    };
    if (!active) return undefined;
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, [items, index, active]);
  async function upload(file) {
    if (!file || !item?.target || busyRef.current) return;
    setError(''); setMessage('');
    if (!/\.(png|jpe?g|pdf)$/i.test(file.name) || file.size > 1572864 || file.size === 0) { setError('Choisissez une image PNG/JPEG ou un PDF de 1,5 Mo maximum.'); return; }
    busyRef.current = true; setBusy(true);
    try {
      const content = await readFile(file);
      const result = await api(`/students/${encodeURIComponent(student.id)}/documents`, { method: 'POST', body: {
        changes: [], expectedUpdatedAt: student.updatedAt, target: item.target, file: { name: file.name, content }
      } });
      const name = item.question.nom + '.' + file.name.split('.').pop().toLowerCase();
      onUpdated(result.student);
      setSelectedKey(name);
      setMessage('La pièce est enregistrée. Son aperçu a été actualisé.');
    } catch (error) { setError(error.message); }
    finally { busyRef.current = false; setBusy(false); }
  }
  return <Modal embedded={embedded} title="Aperçu des pièces du dossier" wide busy={busy} onClose={onClose}>
    <Notice>{error}</Notice><Notice type="success">{message}</Notice>
    {item ? <>
      <div className="ex-preview-toolbar">
        <button className="ex-button" disabled={busy || index === 0} onClick={() => move(index - 1)} aria-label="Pièce précédente (flèche gauche)">← Précédente</button>
        <div className="ex-preview-name" aria-live="polite"><strong>{item.label}</strong>{item.document && <span>{item.document.name}</span>}<span>Pièce {index + 1} / {items.length}</span></div>
        <button className="ex-button" disabled={busy || index === items.length - 1} onClick={() => move(index + 1)} aria-label="Pièce suivante (flèche droite)">Suivante →</button>
      </div>
      <p className="ex-help">Utilisez les flèches ← et → du clavier pour changer de pièce. Échap revient au dossier.</p>
      <div className="ex-document-preview">{item.document
        ? <DocumentContent key={`${item.document.name}:${student.updatedAt}`} studentId={student.id} document={item.document} />
        : <p>Aucune pièce fournie. Vous pouvez ajouter le fichier ici.</p>}</div>
      {item.target && <div className="ex-preview-upload">
        <input ref={fileInput} type="file" accept=".png,.jpg,.jpeg,.pdf" hidden disabled={busy} onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; upload(file); }} />
        <button className="ex-button ex-button--primary" disabled={busy} onClick={() => fileInput.current?.click()}>{busy ? 'Enregistrement…' : item.document ? 'Remplacer cette pièce' : 'Ajouter cette pièce'}</button>
        <small>PNG, JPEG ou PDF · 1,5 Mo maximum</small>
      </div>}
      {busy && <p role="status">Dépôt de la pièce en cours…</p>}
    </> : <p>Aucune pièce prévue dans ce dossier.</p>}
    <div className="ex-modal-footer">
      {Boolean(student.documents?.length) && <a className="ex-button" href={documentsArchiveUrl(student.id)} download><Icon name="download" size={16} />Tout télécharger (.zip)</a>}
      {item?.document && <a className="ex-button" href={documentUrl(student.id, item.document.name)} download><Icon name="download" size={16} />Télécharger cette pièce</a>}
      <button className="ex-button" disabled={busy} onClick={onClose}>Retour au dossier</button>
    </div>
  </Modal>;
}
